import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { useLang } from "../context/LanguageContext";
import { setPageMeta, buildLocalBusinessJsonLd, buildFaqJsonLd, buildBreadcrumbJsonLd } from "../lib/seo";
import { LOCAL_PAGES, CITY_GEO } from "../data/localLandingContent";

const ORIGIN = "https://talentsio.com";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full text-left rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5 hover:border-[#3a3a3d] transition-colors"
      data-testid="local-faq-item"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-white font-medium text-[16px]">{q}</span>
        <ChevronDown size={20} className={`text-neutral-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <p className="text-[15px] text-neutral-400 leading-relaxed mt-3">{a}</p>}
    </button>
  );
};

function buildServiceJsonLd(page, lang) {
  const body = page[lang];
  const cityName = page.city === "stockholm" ? "Stockholm" : "Göteborg";
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: body.h1,
    name: body.metaTitle.split(" | ")[0],
    description: body.metaDesc,
    provider: { "@type": "Organization", name: "Talentsio", url: ORIGIN },
    areaServed: { "@type": "City", name: cityName },
    offers: {
      "@type": "Offer",
      priceCurrency: "SEK",
      url: `${ORIGIN}/BookingRequest?category_group=${page.category_group}`,
      availability: "https://schema.org/InStock",
    },
  };
}

function buildLocalForCity(cityKey) {
  const base = buildLocalBusinessJsonLd();
  const geo = CITY_GEO[cityKey];
  const cityName = cityKey === "stockholm" ? "Stockholm" : "Göteborg";
  if (!geo) return base;
  return {
    ...base,
    areaServed: [{ "@type": "City", name: cityName }, { "@type": "Country", name: "Sweden" }],
    address: {
      "@type": "PostalAddress",
      addressLocality: cityName,
      addressRegion: geo.region,
      postalCode: geo.postal,
      addressCountry: "SE",
    },
    geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lon },
  };
}

const LocalLanding = ({ pageKey }) => {
  const location = useLocation();
  const { setLang } = useLang();
  const isSv = location.pathname.startsWith("/sv/");
  const langKey = isSv ? "sv" : "en";
  const page = LOCAL_PAGES[pageKey];

  useEffect(() => { setLang(langKey); }, [langKey, setLang]);

  useEffect(() => {
    if (!page) return;
    const body = page[langKey];
    const enPath = `/${page.en_slug}`;
    const svPath = `/sv/${page.sv_slug}`;
    const canonical = isSv ? svPath : enPath;
    const jsonLd = [
      buildLocalForCity(page.city),
      buildServiceJsonLd(page, langKey),
      buildFaqJsonLd(body.faq),
      buildBreadcrumbJsonLd([
        { name: isSv ? "Hem" : "Home", path: isSv ? "/sv" : "/" },
        { name: body.h1, path: canonical },
      ]),
    ];
    setPageMeta({
      title: body.metaTitle,
      description: body.metaDesc,
      lang: langKey,
      path: canonical,
      enPath, svPath,
      image: `/og/${page.category_group}.jpg`,
      jsonLd,
    });
  }, [page, langKey, isSv]);

  if (!page) {
    return (
      <PublicLayout>
        <div className="max-w-[640px] mx-auto px-6 pt-[150px] pb-28 text-center">
          <h1 className="text-3xl font-bold text-white">Page not found</h1>
        </div>
      </PublicLayout>
    );
  }

  const body = page[langKey];
  const ctaHref = `/BookingRequest?category_group=${page.category_group}`;
  const ctaLabel = isSv ? "Skicka en gratis förfrågan" : "Post a free request";
  const otherLangPath = isSv ? `/${page.en_slug}` : `/sv/${page.sv_slug}`;

  return (
    <PublicLayout>
      <main className="max-w-[1100px] mx-auto px-6 pt-[110px] pb-16" data-testid="local-landing">
        <nav aria-label="breadcrumbs" className="text-[13px] text-neutral-500 mb-3">
          <Link to={isSv ? "/sv" : "/"} className="text-neutral-500 hover:text-neutral-300">
            {isSv ? "Hem" : "Home"}
          </Link>
          <span className="mx-2 text-neutral-700">›</span>
          <span className="text-neutral-300">{body.h1}</span>
        </nav>

        <div className="text-[12px] tracking-[0.14em] text-[#FF7A2F] font-bold uppercase" data-testid="local-eyebrow">
          {body.eyebrow}
        </div>
        <h1 className="text-[44px] leading-[1.08] font-extrabold tracking-tight text-white mt-4 mb-4 max-w-[820px]"
            data-testid="local-h1">
          {body.h1}
        </h1>
        <p className="text-[19px] leading-[1.65] text-neutral-400 max-w-[820px] mb-6">
          {body.intro}
        </p>

        <div className="flex items-center gap-4 flex-wrap mt-2">
          <Link to={ctaHref}
            className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors"
            data-testid="local-cta">
            {ctaLabel} <ArrowRight size={18} />
          </Link>
          <span className="text-[14px] text-neutral-500">
            {isSv ? "Gratis förfrågan, fast slutpris." : "Free request, fixed final price."}
          </span>
        </div>

        {body.sections.map((sec, i) => (
          <section key={i} className="mt-11 max-w-[820px]" data-testid={`local-section-${i}`}>
            <h2 className="text-white text-[26px] leading-[1.25] font-bold mb-3">{sec.h}</h2>
            <p className="text-neutral-300 text-[17px] leading-[1.7]">{sec.p}</p>
          </section>
        ))}

        <section className="mt-14 max-w-[820px]">
          <h2 className="text-white text-[24px] font-bold mb-4">
            {isSv ? "Vanliga frågor" : "Frequently asked questions"}
          </h2>
          <div className="space-y-3">
            {body.faq.map((f) => <FaqItem key={f.q} {...f} />)}
          </div>
        </section>

        <section className="mt-14 max-w-[820px]">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7 text-center">
            <h2 className="text-white text-[22px] font-bold mb-2">
              {isSv ? "Redo att komma igång" : "Ready to start"}
            </h2>
            <p className="text-neutral-400 text-[15px] leading-relaxed mb-4">{body.metaDesc}</p>
            <Link to={ctaHref}
              className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              data-testid="local-cta-bottom">
              {ctaLabel} <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        <div className="mt-8 text-[13px]">
          <Link to={otherLangPath} className="text-neutral-500 underline hover:text-neutral-300">
            {isSv ? "English" : "Svenska"}
          </Link>
        </div>
      </main>
    </PublicLayout>
  );
};

export default LocalLanding;
